/**
 * Fallback wear masks for models whose ECL_Mask has no edge or cavity bake (the P0 greyboxes: G = B = 0).
 * Per-vertex masks smear across the few big faces of low-poly hulls, so this bakes an exact, density-independent
 * edge field instead: the mesh is de-indexed and every corner carries
 *   `eclEdgeD` (vec3): component k = the triangle's height onto edge k at corner k, 0 elsewhere, so the
 *                     interpolated value is the fragment's distance (metres) to edge k (barycentric × height);
 *   `eclEdgeF` (vec3): per edge, +strength for a convex feature edge, -strength for a concave one, 0 otherwise
 *                     (the same on all three corners, so it is constant over the triangle).
 * The hull shader turns that into sharp, noise-broken wear bands along convex edges and grime along concave ones.
 * Runs once per source geometry at applyShipLook/applyStaticLook (cached); never per frame.
 */
import { BufferAttribute, BufferGeometry, Vector3 } from 'three/webgpu'

export const EDGE_DIST_ATTR = 'eclEdgeD'
export const EDGE_FLAG_ATTR = 'eclEdgeF'

export interface GeometryTraits {
  /** ECL_Mask carries an edge or cavity bake (G or B above zero somewhere). */
  readonly baked: boolean
  /** TEXCOORD_0.v stays inside 0..1 everywhere (trim-sheet UVs, contract §2.7); greybox box UVs do not. */
  readonly trimmed: boolean
}

const MASK_EPS = 1 / 512

/** Reads the mask and UV conventions of a model geometry (pure). */
export function geometryTraits(geometry: BufferGeometry): GeometryTraits {
  const mask = geometry.getAttribute('color')
  let baked = false
  if (mask && mask.itemSize >= 3) {
    for (let i = 0; i < mask.count && !baked; i++) if (mask.getY(i) > MASK_EPS || mask.getZ(i) > MASK_EPS) baked = true
  }
  const uv = geometry.getAttribute('uv')
  let trimmed = uv !== undefined
  if (uv) {
    for (let i = 0; i < uv.count && trimmed; i++) {
      const v = uv.getY(i)
      if (v < -1e-3 || v > 1 + 1e-3) trimmed = false
    }
  }
  return { baked, trimmed }
}

interface EdgeRef {
  tri: number
  /** Edge index inside the triangle = the index of the opposite corner. */
  edge: number
}

function key(p: Vector3): string {
  return `${Math.round(p.x * 1e4)},${Math.round(p.y * 1e4)},${Math.round(p.z * 1e4)}`
}

/**
 * Returns a de-indexed copy of `geometry` with the two edge attributes. `featureAngleDeg` is the smallest
 * dihedral angle that counts as an edge; strength reaches 1 at 60°. Open (boundary) edges count as convex.
 */
export function bakeEdgeField(geometry: BufferGeometry, featureAngleDeg: number): BufferGeometry {
  const g = geometry.index ? geometry.toNonIndexed() : geometry.clone()
  const pos = g.getAttribute('position')
  const triCount = Math.floor(pos.count / 3)
  const p = [new Vector3(), new Vector3(), new Vector3()]
  const normals: Vector3[] = []
  const heights = new Float32Array(triCount * 3)
  const keys: string[] = []
  const edges = new Map<string, EdgeRef[]>()
  const e1 = new Vector3()
  const e2 = new Vector3()
  for (let t = 0; t < triCount; t++) {
    for (let k = 0; k < 3; k++) {
      p[k].fromBufferAttribute(pos, t * 3 + k)
      keys.push(key(p[k]))
    }
    e1.subVectors(p[1], p[0])
    e2.subVectors(p[2], p[0])
    const n = new Vector3().crossVectors(e1, e2)
    const area2 = n.length()
    normals.push(area2 > 0 ? n.divideScalar(area2) : n.set(0, 0, 1))
    for (let k = 0; k < 3; k++) {
      // Edge k joins the two corners other than k.
      const a = p[(k + 1) % 3]
      const b = p[(k + 2) % 3]
      const len = a.distanceTo(b)
      heights[t * 3 + k] = len > 0 ? area2 / len : 0
      const ka = keys[t * 3 + ((k + 1) % 3)]
      const kb = keys[t * 3 + ((k + 2) % 3)]
      const ek = ka < kb ? `${ka}|${kb}` : `${kb}|${ka}`
      let list = edges.get(ek)
      if (!list) {
        list = []
        edges.set(ek, list)
      }
      list.push({ tri: t, edge: k })
    }
  }

  const minAngle = (featureAngleDeg * Math.PI) / 180
  const fullAngle = Math.PI / 3
  const flags = new Float32Array(triCount * 3)
  const mid = new Vector3()
  const other = new Vector3()
  for (const list of edges.values()) {
    for (const ref of list) {
      const t = ref.tri
      const k = ref.edge
      if (list.length === 1) {
        flags[t * 3 + k] = 1
        continue
      }
      mid.fromBufferAttribute(pos, t * 3 + ((k + 1) % 3)).add(other.fromBufferAttribute(pos, t * 3 + ((k + 2) % 3))).multiplyScalar(0.5)
      let best = 0
      for (const o of list) {
        if (o.tri === t) continue
        const cos = Math.min(1, Math.max(-1, normals[t].dot(normals[o.tri])))
        const angle = Math.acos(cos)
        if (angle < minAngle || angle <= Math.abs(best) * fullAngle) continue
        // Convex when the neighbour's far corner lies below this triangle's plane.
        other.fromBufferAttribute(pos, o.tri * 3 + o.edge).sub(mid)
        const strength = Math.min(1, angle / fullAngle)
        best = other.dot(normals[t]) < 0 ? strength : -strength
      }
      flags[t * 3 + k] = best
    }
  }

  const dist = new Float32Array(triCount * 9)
  const flag = new Float32Array(triCount * 9)
  for (let t = 0; t < triCount; t++) {
    for (let c = 0; c < 3; c++) {
      const o = (t * 3 + c) * 3
      dist[o + c] = heights[t * 3 + c]
      flag[o] = flags[t * 3]
      flag[o + 1] = flags[t * 3 + 1]
      flag[o + 2] = flags[t * 3 + 2]
    }
  }
  g.setAttribute(EDGE_DIST_ATTR, new BufferAttribute(dist, 3))
  g.setAttribute(EDGE_FLAG_ATTR, new BufferAttribute(flag, 3))
  g.name = `${geometry.name}:edgeField`
  return g
}

/** CPU mirror of the shader's convex-edge proximity at barycentric `bary` of triangle `tri` (tests and tools). */
export function edgeProximity(g: BufferGeometry, tri: number, bary: readonly [number, number, number], width: number): { convex: number; concave: number } {
  const d = g.getAttribute(EDGE_DIST_ATTR)
  const f = g.getAttribute(EDGE_FLAG_ATTR)
  let convex = 0
  let concave = 0
  for (let k = 0; k < 3; k++) {
    // Distance to edge k interpolates only corner k's component.
    const comp = k === 0 ? d.getX(tri * 3 + k) : k === 1 ? d.getY(tri * 3 + k) : d.getZ(tri * 3 + k)
    const dk = bary[k] * comp
    const fk = k === 0 ? f.getX(tri * 3) : k === 1 ? f.getY(tri * 3) : f.getZ(tri * 3)
    const near = 1 - Math.min(1, Math.max(0, dk / width))
    if (fk > 0) convex = Math.max(convex, fk * near)
    else if (fk < 0) concave = Math.max(concave, -fk * near)
  }
  return { convex, concave }
}
